import { Injectable, inject } from '@angular/core';
import { BehaviorSubject } from 'rxjs';
import * as THREE from 'three';
import { SessionEngineService } from './session-engine.service';
import { SessionDataService } from './session-data.service';
import { TelemetryFrame } from '../../../models/session.model';

@Injectable()
export class SessionHeatmapService {
  private engine = inject(SessionEngineService);
  private dataService = inject(SessionDataService);

  public raycaster = new THREE.Raycaster();
  public mouse = new THREE.Vector2();
  
  public selectedObjectNombre$ = new BehaviorSubject<string | null>(null);
  public heatmapStats$ = new BehaviorSubject<any>(null);

  // Global object stats
  public objectStats: { [name: string]: { hits: number, interactions: number, hitPercentage: number, interactionPercentage: number } } = {};
  public globalStats = { totalHits: 0, totalInteractions: 0 };

  
  private heatmapGroup = new THREE.Group();
  private gazeHitDotGeom = new THREE.SphereGeometry(0.02, 6, 6);
  private gazeHitMaterial = new THREE.MeshBasicMaterial({ color: 0xff6600, transparent: true, opacity: 0.6, depthTest: false, depthWrite: false });

  constructor() {
    this.engine.sceneReady$.subscribe(ready => {
      if (ready && this.engine.scene) {
        // We do not add heatmapGroup to scene here, we attach it to the specific object
      }
    });
  }

  
  public selectObject(name: string, session: any) {
    this.selectedObjectNombre$.next(name);
    this.engine.selectedObjectNombre = name;
    
    // The engine should hide gaze trails, wait, engine has gazeTrailGroup?
    // We should let engine know to dim objects
    this.engine.highlightObject(name);

    const sceneObj = session?.sceneSnapshot?.objects?.find((o: any) => o.name === name);
    if (sceneObj?.rotationOffset) {
      this.engine.debugRotX = sceneObj.rotationOffset.x || 0;
      this.engine.debugRotY = sceneObj.rotationOffset.y || 0;
      this.engine.debugRotZ = sceneObj.rotationOffset.z || 0;
    } else {
      this.engine.debugRotX = 0;
      this.engine.debugRotY = 0;
      this.engine.debugRotZ = 0;
    }

    if (sceneObj?.positionOffset) {
      this.engine.debugPosX = sceneObj.positionOffset.x || 0;
      this.engine.debugPosY = sceneObj.positionOffset.y || 0;
      this.engine.debugPosZ = sceneObj.positionOffset.z || 0;
    } else {
      this.engine.debugPosX = 0;
      this.engine.debugPosY = 0;
      this.engine.debugPosZ = 0;
    }
  }

  public clearSelection() {
    this.selectedObjectNombre$.next(null);
    this.engine.selectedObjectNombre = null;
    this.engine.highlightObject(null);
    while (this.heatmapGroup.children.length > 0) {
      this.heatmapGroup.remove(this.heatmapGroup.children[0]);
    }
    this.heatmapStats$.next(null);
    if (this.engine.renderer && this.engine.scene && this.engine.camera) {
      this.engine.renderer.render(this.engine.scene, this.engine.camera);
    }
  }

  public buildLocalHeatmap(objectNombre: string, telemetryFrames: TelemetryFrame[], breadcrumbs: any[], sceneSnapshot: any) {
    // Clear old
    if (this.heatmapGroup.parent) {
      this.heatmapGroup.parent.remove(this.heatmapGroup);
    }
    while (this.heatmapGroup.children.length > 0) {
      this.heatmapGroup.remove(this.heatmapGroup.children[0]);
    }

    let totalHits = 0;
    let interactions = 0;

    // Count interactions from breadcrumbs
    if (breadcrumbs) {
      interactions = breadcrumbs.filter(b => b.category === 'Interaction' && b.message.includes(objectNombre)).length;
    }

    // We will collect points and their local scales to calculate density
    const hitPoints: { pos: THREE.Vector3, scale: THREE.Vector3 }[] = [];

    // Iterate ALL frames to build the full history heatmap for this object
    telemetryFrames.forEach(frame => {
      if (frame.gazeTarget === objectNombre && frame.gazeHitPoint) {
        totalHits++;

        // The gaze hit is in World space. We need to transform it to Local space 
        // based on the object's transform at THIS EXACT FRAME.
        const hitWorld = new THREE.Vector3(frame.gazeHitPoint.x, frame.gazeHitPoint.y, -frame.gazeHitPoint.z);

        const objPose = (frame.dynamicObjects && frame.dynamicObjects[objectNombre]) ? frame.dynamicObjects[objectNombre] : null;

        const tempObj = new THREE.Object3D();
        const snapObj = sceneSnapshot?.objects?.find((o: any) => o.name === objectNombre);

        if (objPose) {
          tempObj.position.set(objPose.x, objPose.y, -objPose.z);
          const baseQuat = new THREE.Quaternion(-objPose.qx, -objPose.qy, objPose.qz, objPose.qw);
          
          if (snapObj?.rotationOffset) {
            const offsetEuler = new THREE.Euler(
              snapObj.rotationOffset.x * Math.PI / 180,
              snapObj.rotationOffset.y * Math.PI / 180,
              snapObj.rotationOffset.z * Math.PI / 180,
              'XYZ'
            );
            const offsetQuat = new THREE.Quaternion().setFromEuler(offsetEuler);
            baseQuat.multiply(offsetQuat);
          }
          tempObj.quaternion.copy(baseQuat);

          if (snapObj?.positionOffset) {
            const posOffsetVec = new THREE.Vector3(snapObj.positionOffset.x, snapObj.positionOffset.y, snapObj.positionOffset.z);
            posOffsetVec.applyQuaternion(tempObj.quaternion);
            tempObj.position.add(posOffsetVec);
          }

          if (objPose.sx !== undefined) {
            tempObj.scale.set(objPose.sx, objPose.sy, objPose.sz);
          } else if (snapObj && snapObj.scaleX !== undefined) {
            tempObj.scale.set(Math.max(0.001, snapObj.scaleX), Math.max(0.001, snapObj.scaleY), Math.max(0.001, snapObj.scaleZ));
          } else {
            tempObj.scale.set(1, 1, 1);
          }
        } else if (snapObj) {
          tempObj.position.set(snapObj.posX || 0, snapObj.posY || 0, -(snapObj.posZ || 0));
          const baseQuat = new THREE.Quaternion(-(snapObj.rotX || 0), -(snapObj.rotY || 0), (snapObj.rotZ || 0), (snapObj.rotW || 1));
          
          if (snapObj.rotationOffset) {
            const offsetEuler = new THREE.Euler(
              snapObj.rotationOffset.x * Math.PI / 180,
              snapObj.rotationOffset.y * Math.PI / 180,
              snapObj.rotationOffset.z * Math.PI / 180,
              'XYZ'
            );
            const offsetQuat = new THREE.Quaternion().setFromEuler(offsetEuler);
            baseQuat.multiply(offsetQuat);
          }
          tempObj.quaternion.copy(baseQuat);

          if (snapObj.positionOffset) {
            const posOffsetVec = new THREE.Vector3(snapObj.positionOffset.x, snapObj.positionOffset.y, snapObj.positionOffset.z);
            posOffsetVec.applyQuaternion(tempObj.quaternion);
            tempObj.position.add(posOffsetVec);
          }
          
          tempObj.scale.set(
            snapObj.scaleX !== undefined ? Math.max(0.001, snapObj.scaleX) : 1, 
            snapObj.scaleY !== undefined ? Math.max(0.001, snapObj.scaleY) : 1, 
            snapObj.scaleZ !== undefined ? Math.max(0.001, snapObj.scaleZ) : 1
          );
        } else {
          // No transform data, fallback to origin
        }

        tempObj.updateMatrixWorld(true);

        const invMat = new THREE.Matrix4().copy(tempObj.matrixWorld).invert();
        let localHit = hitWorld.clone().applyMatrix4(invMat);

        // Universal surface snapping logic matching addGazeHitDot
        const targetChild = this.engine.sceneGroup.children.find((c: any) => c.name === objectNombre);
        if (targetChild) {
          let directionLocal = localHit.clone();
          if (directionLocal.lengthSq() > 0.00001) {
            directionLocal.normalize();
            const rayOriginLocal = directionLocal.clone().multiplyScalar(10); // Start far outside

            // Transform local ray to tempObj's current world space
            const rayOriginWorld = rayOriginLocal.clone().applyMatrix4(tempObj.matrixWorld);
            const zeroWorld = new THREE.Vector3(0, 0, 0).applyMatrix4(tempObj.matrixWorld);
            const rayDirWorld = zeroWorld.clone().sub(rayOriginWorld).normalize();
            
            // We must raycast against targetChild in its CURRENT world transform!
            const rayOriginWorldCurrent = rayOriginLocal.clone().applyMatrix4(targetChild.matrixWorld);
            const zeroWorldCurrent = new THREE.Vector3(0, 0, 0).applyMatrix4(targetChild.matrixWorld);
            const rayDirWorldCurrent = zeroWorldCurrent.clone().sub(rayOriginWorldCurrent).normalize();
            
            const currentRaycaster = new THREE.Raycaster(rayOriginWorldCurrent, rayDirWorldCurrent);
            const intersects = currentRaycaster.intersectObject(targetChild, true);

            const validHit = intersects.find(i => i.object.name !== 'heatmapDot' && i.object.name !== 'gazeDot' && i.object.type === 'Mesh');
            if (validHit) {
              localHit = targetChild.worldToLocal(validHit.point.clone()); // perfectly snapped to the geometry surface
            }
          }
        }

        hitPoints.push({
          pos: localHit,
          scale: new THREE.Vector3(1 / (tempObj.scale.x || 1), 1 / (tempObj.scale.y || 1), 1 / (tempObj.scale.z || 1))
        });
      }
    });

    if (hitPoints.length === 0) {
      this.heatmapStats$.next({ hits: totalHits, interactions });
      return;
    }

    // Compute density
    const densities = new Float32Array(hitPoints.length);
    let maxDensity = 1;
    const radiusSq = 0.15 * 0.15; // 15cm grouping radius in unscaled space

    const targetChild = this.engine.sceneGroup.children.find((c: any) => c.name === objectNombre);

    for (let i = 0; i < hitPoints.length; i++) {
      let density = 0;
      for (let j = 0; j < hitPoints.length; j++) {
        if (hitPoints[i].pos.distanceToSquared(hitPoints[j].pos) < radiusSq) {
          density++;
        }
      }
      densities[i] = density;
      if (density > maxDensity) maxDensity = density;
    }

    // Create InstancedMesh (adjusted radius 0.008 based on feedback)
    const localGeom = new THREE.SphereGeometry(0.008, 4, 4);
    const localMat = new THREE.MeshBasicMaterial({ transparent: true, opacity: 0.8, depthTest: false, depthWrite: false });
    const instancedMesh = new THREE.InstancedMesh(localGeom, localMat, hitPoints.length);
    instancedMesh.name = 'heatmapDot';
    instancedMesh.raycast = () => { };
    instancedMesh.renderOrder = 999; // Ensure dots render on top of transparent materials

    const dummy = new THREE.Object3D();
    const color = new THREE.Color();

    const colors = [
      new THREE.Color(0x0000ff), // Blue (Cold)
      new THREE.Color(0x00aaff), // Light Blue
      new THREE.Color(0x00ff00), // Green
      new THREE.Color(0xaaff00), // Yellow-Green
      new THREE.Color(0xffff00), // Yellow
      new THREE.Color(0xff8800), // Orange
      new THREE.Color(0xff4400), // Dark Orange
      new THREE.Color(0xff0000)  // Red (Hot)
    ];

    for (let i = 0; i < hitPoints.length; i++) {
      dummy.position.copy(hitPoints[i].pos);
      dummy.scale.copy(hitPoints[i].scale); // Mathematically cancels out the group's non-uniform scale!
      dummy.updateMatrix();
      instancedMesh.setMatrixAt(i, dummy.matrix);

      // Map density to color gradient (exponent 0.4 aggressively boosts low densities into warm colors)
      const value = Math.pow(densities[i] / maxDensity, 0.4);
      
      const idx = value * (colors.length - 1);
      const colorIdx = Math.max(0, Math.min(Math.floor(idx), colors.length - 2));
      const f = idx - colorIdx;

      color.copy(colors[colorIdx]).lerp(colors[colorIdx + 1], f);
      instancedMesh.setColorAt(i, color);
    }

    instancedMesh.instanceMatrix.needsUpdate = true;
    if (instancedMesh.instanceColor) instancedMesh.instanceColor.needsUpdate = true;

    this.heatmapGroup.add(instancedMesh);

    // Attach heatmapGroup to the targetChild (group) exactly like older.ts did!
    if (targetChild) {
      this.heatmapGroup.position.set(0, 0, 0);
      this.heatmapGroup.quaternion.identity();
      this.heatmapGroup.scale.set(1, 1, 1);
      targetChild.add(this.heatmapGroup);
    } else {
      this.engine.scene.add(this.heatmapGroup);
    }

    this.heatmapStats$.next({ hits: totalHits, interactions });
    if (this.engine.renderer && this.engine.scene && this.engine.camera) {
      this.engine.renderer.render(this.engine.scene, this.engine.camera);
    }
  }

  public refreshObjectTransform(session: any, frameIdx: number) {
    const objectNombre = this.selectedObjectNombre$.value;
    if (!objectNombre) return;

    const snapObj = session?.sceneSnapshot?.objects?.find((o: any) => o.name === objectNombre);
    const frames = this.dataService.telemetryFrames$.value;
    const frame = frames[frameIdx];

    const currentPose = (frame?.dynamicObjects && frame.dynamicObjects[objectNombre]) ? frame.dynamicObjects[objectNombre] : null;

    const tempObj = new THREE.Object3D();
    if (currentPose) {
      tempObj.position.set(currentPose.x, currentPose.y, -currentPose.z);
      tempObj.quaternion.set(-currentPose.qx, -currentPose.qy, currentPose.qz, currentPose.qw);
      if (currentPose.sx !== undefined) {
        tempObj.scale.set(currentPose.sx, currentPose.sy, currentPose.sz);
      } else if (snapObj && snapObj.transform) {
        tempObj.scale.set(snapObj.transform.scale.x, snapObj.transform.scale.y, snapObj.transform.scale.z);
      }
    } else if (snapObj && snapObj.transform) {
      tempObj.position.set(snapObj.transform.position.x, snapObj.transform.position.y, -snapObj.transform.position.z);
      tempObj.quaternion.set(-snapObj.transform.rotation.x, -snapObj.transform.rotation.y, snapObj.transform.rotation.z, snapObj.transform.rotation.w);
      tempObj.scale.set(snapObj.transform.scale.x, snapObj.transform.scale.y, snapObj.transform.scale.z);
    } else {
      tempObj.position.set(0,0,0);
      tempObj.quaternion.identity();
      tempObj.scale.set(1,1,1);
    }

    tempObj.updateMatrixWorld(true);

    this.heatmapGroup.children.forEach(child => {
      if (child.userData['isHeatmapPoint'] && child.userData['originalLocalPos']) {
        const localPos = child.userData['originalLocalPos'] as THREE.Vector3;
        child.position.copy(localPos);
      }
    });

    if (this.engine.renderer && this.engine.scene && this.engine.camera) {
      this.engine.renderer.render(this.engine.scene, this.engine.camera);
    }
  }

  public computeGlobalStats(telemetryFrames: TelemetryFrame[], breadcrumbs: any[]) {
    this.objectStats = {};
    this.globalStats = { totalHits: 0, totalInteractions: 0 };
    
    // First pass: count hits
    telemetryFrames.forEach(frame => {
      if (frame.gazeTarget) {
        if (!this.objectStats[frame.gazeTarget]) {
          this.objectStats[frame.gazeTarget] = { hits: 0, interactions: 0, hitPercentage: 0, interactionPercentage: 0 };
        }
        this.objectStats[frame.gazeTarget].hits++;
        this.globalStats.totalHits++;
      }
    });

    // Second pass: count interactions from breadcrumbs
    if (breadcrumbs) {
      breadcrumbs.forEach(b => {
        if (b.category === 'Interaction') {
          // Identify which object was interacted with (assuming the message contains the object name)
          Object.keys(this.objectStats).forEach(objName => {
            if (b.message.includes(objName)) {
              this.objectStats[objName].interactions++;
              this.globalStats.totalInteractions++;
            }
          });
        }
      });
    }

    // Third pass: calculate percentages
    Object.keys(this.objectStats).forEach(key => {
      const stats = this.objectStats[key];
      stats.hitPercentage = this.globalStats.totalHits > 0 ? (stats.hits / this.globalStats.totalHits) * 100 : 0;
      stats.interactionPercentage = this.globalStats.totalInteractions > 0 ? (stats.interactions / this.globalStats.totalInteractions) * 100 : 0;
    });
  }
}
